// ===================================================
// GAM Command Center — notification sounds for the internal chat.
// Synthesised with the Web Audio API rather than shipped as audio files: no asset
// fetch, nothing for a CSP to block, and the "pick a sound" list Gal asked for costs
// a few oscillator envelopes instead of a media pipeline.
// Spec: docs/specs/chat-substrate-stage1-internal-alerts.md §5.3
// ===================================================

export interface ChatSound {
  id: string;
  label: string;
  /** [frequency Hz, start offset s, duration s] per tone */
  tones: [number, number, number][];
  type: OscillatorType;
}

export const CHAT_SOUNDS: ChatSound[] = [
  { id: "ping", label: "נקישה", type: "sine", tones: [[880, 0, 0.12]] },
  { id: "bell", label: "פעמון", type: "sine", tones: [[1318, 0, 0.09], [1760, 0.07, 0.28]] },
  { id: "double", label: "כפולה", type: "triangle", tones: [[660, 0, 0.08], [660, 0.13, 0.08]] },
  { id: "rise", label: "עלייה", type: "sine", tones: [[523, 0, 0.1], [659, 0.08, 0.1], [784, 0.16, 0.18]] },
  { id: "soft", label: "רכה", type: "sine", tones: [[392, 0, 0.3]] },
  { id: "none", label: "ללא צליל", type: "sine", tones: [] },
];

export const DEFAULT_SOUND_ID = "bell";

let ctx: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  return ctx;
}

/**
 * Play one of the sounds above. Safe to call before any user gesture — browsers
 * suspend the context until then, which silences the tone rather than throwing.
 */
export function playChatSound(soundId: string, volume = 0.25): void {
  const sound = CHAT_SOUNDS.find((s) => s.id === soundId);
  if (!sound || sound.tones.length === 0) return;

  const ac = audioContext();
  if (!ac) return;
  if (ac.state === "suspended") void ac.resume();

  const now = ac.currentTime;
  for (const [freq, offset, duration] of sound.tones) {
    const osc = ac.createOscillator();
    const gain = ac.createGain();
    osc.type = sound.type;
    osc.frequency.value = freq;

    // Short attack + exponential decay — a linear cut clicks audibly.
    const start = now + offset;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    osc.connect(gain).connect(ac.destination);
    osc.start(start);
    osc.stop(start + duration + 0.02);
  }
}
